import AsyncStorage from "@react-native-async-storage/async-storage";

/** Bump when the what's new copy changes so returning users see the screen again. */
export const WHATS_NEW_VERSION = 1;

const SEEN_VERSION_KEY = "whatsNew.seenVersion";

export type WhatsNewDecision = "show" | "skip" | "skip-and-mark";

/**
 * Fresh installs should not see what's new. Existing installs (upgrade) should,
 * until they dismiss the current content version.
 *
 * `hadTokenOnLaunch` is the upgrade signal for the first rollout: a restored
 * session means this device already used a previous build.
 */
export function shouldShowWhatsNew({
    hadTokenOnLaunch,
    seenVersion,
    currentVersion = WHATS_NEW_VERSION,
}: {
    hadTokenOnLaunch: boolean;
    seenVersion: number | null;
    currentVersion?: number;
}): WhatsNewDecision {
    if (seenVersion != null && seenVersion >= currentVersion) {
        return "skip";
    }

    if (seenVersion == null && !hadTokenOnLaunch) {
        return "skip-and-mark";
    }

    return "show";
}

export async function getSeenWhatsNewVersion(): Promise<number | null> {
    const value = await AsyncStorage.getItem(SEEN_VERSION_KEY);
    if (value == null || value === "") {
        return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

export async function markWhatsNewSeen(
    version: number = WHATS_NEW_VERSION,
): Promise<void> {
    await AsyncStorage.setItem(SEEN_VERSION_KEY, String(version));
}

/** True when the what's new screen should open. Marks a fresh install as seen. */
export async function resolveWhatsNew(hadTokenOnLaunch: boolean): Promise<boolean> {
    const decision = shouldShowWhatsNew({
        hadTokenOnLaunch,
        seenVersion: await getSeenWhatsNewVersion(),
    });
    if (decision === "skip-and-mark") {
        await markWhatsNewSeen();
        return false;
    }
    return decision === "show";
}
